import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Search, ArrowRight, CornerDownLeft } from 'lucide-react';

export interface Command {
  group: string;
  title: string;
  hint?: string;
  /** Route to navigate to when selected. */
  to: string;
  /** Extra search keywords (space-separated). */
  keywords?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Application-specific command list. apps/web and apps/admin pass their own. */
  commands: Command[];
  /** Footer label, e.g. `Edupra · ⌘K`. */
  brandLabel?: string;
  /** Input placeholder. */
  placeholder?: string;
}

export function CommandPalette({
  open,
  onClose,
  commands,
  brandLabel = 'Edupra · ⌘K',
  placeholder = 'Search pages, actions, reports…',
}: Props) {
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return commands;
    return commands.filter((c) =>
      `${c.title} ${c.group} ${c.keywords ?? ''}`.toLowerCase().includes(t),
    );
  }, [q, commands]);

  // Reset state on open/close
  useEffect(() => {
    if (open) {
      setQ('');
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open]);

  useEffect(() => {
    setActive(0);
  }, [q]);

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const cmd = filtered[active];
      if (cmd) {
        navigate(cmd.to);
        onClose();
      }
    } else if (e.key === 'Escape') {
      onClose();
    }
  }

  // Scroll active item into view
  useEffect(() => {
    const root = listRef.current;
    if (!root) return;
    const el = root.querySelector<HTMLElement>(`[data-idx="${active}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  if (!open) return null;

  // Group filtered list while preserving order
  const groups: { name: string; items: { cmd: Command; idx: number }[] }[] = [];
  filtered.forEach((cmd, idx) => {
    let g = groups.find((x) => x.name === cmd.group);
    if (!g) {
      g = { name: cmd.group, items: [] };
      groups.push(g);
    }
    g.items.push({ cmd, idx });
  });

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh] ed-fade-in"
      style={{ background: 'rgb(0 0 0 / 0.55)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[640px] rounded-xl border shadow-2xl overflow-hidden"
        style={{
          background: 'var(--bg-raised)',
          borderColor: 'var(--border-line)',
        }}
      >
        <div
          className="flex items-center gap-2 border-b px-3 py-2.5"
          style={{ borderColor: 'var(--border-hair)' }}
        >
          <Search className="h-4 w-4" style={{ color: 'var(--fg-low)' }} strokeWidth={1.75} />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            className="flex-1 bg-transparent text-[14px] outline-none placeholder:opacity-60"
            style={{ color: 'var(--fg-high)' }}
          />
          <span
            className="ed-kbd"
            style={{ color: 'var(--fg-faint)' }}
          >
            ESC
          </span>
        </div>

        <div
          ref={listRef}
          className="max-h-[420px] overflow-y-auto p-1.5"
        >
          {filtered.length === 0 ? (
            <div
              className="py-10 text-center text-[12.5px]"
              style={{ color: 'var(--fg-low)' }}
            >
              No matches for &ldquo;{q}&rdquo;
            </div>
          ) : (
            groups.map((g) => (
              <div key={g.name} className="mb-1">
                <div
                  className="px-2 py-1 text-[10px] uppercase tracking-[0.08em]"
                  style={{ color: 'var(--fg-faint)' }}
                >
                  {g.name}
                </div>
                {g.items.map(({ cmd, idx }) => (
                  <button
                    key={cmd.to + idx}
                    data-idx={idx}
                    onMouseEnter={() => setActive(idx)}
                    onClick={() => {
                      navigate(cmd.to);
                      onClose();
                    }}
                    className="group flex w-full items-center justify-between rounded-md px-2 py-2 text-left transition-colors"
                    style={{
                      background: idx === active ? 'var(--bg-sunken)' : 'transparent',
                      color: 'var(--fg-high)',
                    }}
                  >
                    <span className="flex items-center gap-2 text-[13px]">
                      <ArrowRight className="h-3 w-3" style={{ color: 'var(--fg-faint)' }} strokeWidth={2} />
                      {cmd.title}
                    </span>
                    {idx === active && (
                      <CornerDownLeft
                        className="h-3.5 w-3.5"
                        style={{ color: 'var(--fg-low)' }}
                        strokeWidth={1.75}
                      />
                    )}
                  </button>
                ))}
              </div>
            ))
          )}
        </div>

        <div
          className="flex items-center justify-between border-t px-3 py-2 text-[10.5px]"
          style={{
            borderColor: 'var(--border-hair)',
            color: 'var(--fg-faint)',
            background: 'var(--bg-surface)',
          }}
        >
          <span className="flex items-center gap-3">
            <span><span className="ed-kbd">↑↓</span> navigate</span>
            <span><span className="ed-kbd">↵</span> open</span>
          </span>
          <span>{brandLabel}</span>
        </div>
      </div>
    </div>
  );
}
